import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import './GroupsDropdown.css';
import BASE_URL from '../utils/api';

// API helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  for (const base of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const cfg = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) cfg.body = JSON.stringify(data);
      const res = await fetch(base, cfg);
      if (!res) continue;
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json')) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text;
      }
    } catch (e) { /* try next base */ }
  }
  throw new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

export default function MeusGruposDropdown() {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const carregarGrupos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const meuId = getUsuarioSistemaId();
      if (!meuId) {
        setGrupos([]);
        setError('Usuário não autenticado');
        return;
      }

      // Buscar todos os grupos (igual ao sidebar)
      const data = await apiCall('Grupo', 'GET');
      const todosGrupos = Array.isArray(data) ? data : (data?.Grupos || data?.grupos || []);

      // Buscar grupos que o usuário participa (via UsuariosGrupo/usuario/{id})
      let gruposParticipo = [];
      try {
        const usuarioGrupos = await apiCall(`UsuariosGrupo/usuario/${meuId}`, 'GET');
        const arrUG = Array.isArray(usuarioGrupos) ? usuarioGrupos : [];
        gruposParticipo = arrUG.map(ug => ug.GrupoId || ug.grupoId);
      } catch (e) {
        console.warn('Não foi possível buscar UsuariosGrupo:', e);
      }

      // Filtrar: grupos que eu criei OU grupos que eu participo
      const meusGrupos = todosGrupos.filter(g => {
        const grupoId = g.Id || g.id;
        const criadorId = g.UsuarioSistemaId || g.usuarioSistemaId || g.CriadoPor || g.criadoPor;
        // Grupo que eu criei
        if (criadorId === meuId) return true;
        // Grupo que eu participo
        if (gruposParticipo.includes(grupoId)) return true;
        return false;
      });

      // Para cada grupo, buscar a imagem se tiver
      const gruposComImagem = await Promise.all(meusGrupos.map(async (g) => {
        const gid = g.Id || g.id;
        let imagemUrl = null;
        
        // Verificar se tem FotoGrupo base64
        if (g.FotoGrupo || g.fotoGrupo) {
          const foto = g.FotoGrupo || g.fotoGrupo;
          if (typeof foto === 'string' && foto.length > 0) {
            imagemUrl = foto.startsWith('data:') ? foto : `data:image/jpeg;base64,${foto}`;
          } else if (Array.isArray(foto) && foto.length > 0) {
            const bytes = Uint8Array.from(foto);
            const blob = new Blob([bytes], { type: 'image/jpeg' });
            imagemUrl = URL.createObjectURL(blob);
          }
        }
        
        // Se não tiver imagem no objeto, tentar buscar via API
        if (!imagemUrl) {
          try {
            const fotoRes = await apiCall(`Grupo/${gid}/foto`, 'GET');
            if (fotoRes && typeof fotoRes === 'string' && fotoRes.length > 0) {
              imagemUrl = fotoRes.startsWith('data:') ? fotoRes : `data:image/jpeg;base64,${fotoRes}`;
            }
          } catch { /* sem foto */ }
        }

        const criadorId = g.UsuarioSistemaId || g.usuarioSistemaId || g.CriadoPor || g.criadoPor;
        return {
          ...g,
          imagemUrl,
          isDono: criadorId === meuId
        };
      }));

      setGrupos(gruposComImagem);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
      setError('Erro ao carregar grupos');
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarGrupos();
  }, [carregarGrupos]);

  // Ao clicar em um grupo, navegar para GrupoDropDown com o grupo selecionado
  const handleGrupoClick = (grupo) => {
    navigate('/sidebar/GrupoDropDown', { state: { selectedGroup: grupo } });
  };

  // Obter inicial do nome do grupo
  const getInitial = (name) => {
    if (!name) return '?';
    const clean = name.replace(/[^a-zA-ZÀ-ÿ]/g, '');
    return clean.charAt(0).toUpperCase() || '?';
  };

  return (
    <div className="groups-dropdown-overlay">
      <div className="groups-dropdown-card">
        <div className="meus-grupos-header">
          <h2 style={{ margin: 0, color: 'rgb(235, 133, 0)', fontWeight: 700 }}>Meus Grupos</h2>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            Carregando grupos...
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#d32f2f' }}>
            {error}
          </div>
        )}

        {!loading && !error && grupos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            Você não participa de nenhum grupo ainda.
          </div>
        )}

        {!loading && !error && grupos.length > 0 && (
          <div className="meus-grupos-grid">
            {grupos.map(g => {
              const gid = g.Id || g.id;
              const nome = g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome || 'Grupo';
              const descricao = g.DescricaoGrupo || g.descricaoGrupo || g.Descricao || g.descricao || '';
              
              return (
                <div
                  key={gid}
                  className="meus-grupos-card"
                  onClick={() => handleGrupoClick(g)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleGrupoClick(g); }}
                >
                  <div className="meus-grupos-card-image">
                    {g.imagemUrl ? (
                      <img src={g.imagemUrl} alt={nome} />
                    ) : (
                      <div className="meus-grupos-card-placeholder">
                        {getInitial(nome)}
                      </div>
                    )}
                  </div>
                  <div className="meus-grupos-card-content">
                    <h3 className="meus-grupos-card-title">{nome}</h3>
                    {descricao && (
                      <p className="meus-grupos-card-desc">
                        {descricao.length > 100 ? descricao.substring(0, 100) + '...' : descricao}
                      </p>
                    )}
                    {g.isDono && (
                      <span className="meus-grupos-card-badge">Dono</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
